import React, { useContext } from "react";
import { LoadingContext, ErrorContext } from "/src/helpers/contexts/agentContext";

// Shared across hook instances: dedupes identical GET requests that are already in flight, so a
// double-render / quick re-mount doesn't fire the same request twice.
const inFlightGets = new Map();

export function useApi() {
    const [currentLoading, setCurrentLoading] = useContext(LoadingContext);

    const [currentError, setCurrentError] = useContext(ErrorContext);

    const get = async (path, queryString, hideLoading = false, message = "Loading...") => {
        const url = `${path}?${queryString}`;
        if (inFlightGets.has(url)) {
            return inFlightGets.get(url);
        }

        const requestPromise = (async () => {
            let postsData = null;
            if (!hideLoading) {
                setCurrentLoading(true, message);
            }
            try {
                const response = await fetch(url);
                if (!response.ok) {
                    setCurrentError(`API Error: ${response.status} on ${url}`);
                }
                postsData = await response.json();
            } catch (parseError) {
                console.error("error", JSON.stringify(parseError));
                setCurrentError(parseError.message);
            }
            if (!hideLoading) {
                setCurrentLoading(false);
            }
            return postsData;
        })();

        inFlightGets.set(url, requestPromise);
        try {
            return await requestPromise;
        } finally {
            inFlightGets.delete(url);
        }
    };

    const post = async (path, queryString, data, hideLoading = false, message = "Loading...") => {
        if (!hideLoading) {
            setCurrentLoading(true, message);
        }

        // The finally guarantees the full-screen overlay never sticks when the response is not
        // JSON (e.g. a 503 with a plain-text body used to leave "Updating payment" spinning).
        try {
            const response = await fetch(`${path}?${queryString}`, {
                headers: {
                    Accept: "application/json",
                    "Content-Type": "application/json",
                },
                method: "POST",
                body: JSON.stringify(data),
            });
            if (!response.ok) {
                setCurrentError(`API Error: ${response.status} on ${path}?${queryString}`);
            }
            return await response.json();
        } finally {
            if (!hideLoading) {
                setCurrentLoading(false);
            }
        }
    };
    const api = {
        doPackageSearch: async (apiSearchParams) => {
            return await get("/api/package/search", apiSearchParams, true);
        },
        doHotelOnlySearch: async (apiSearchParams) => {
            return await get('/api/hotelonly/search', apiSearchParams, true);
        },
        // Villa (/V/) and Villa + Flights (/VF/): the Hotel Only / Package searches' parameters, from Almanac
        doVillaSearch: async (apiSearchParams) => {
            return await get('/api/villa/search', apiSearchParams, true);
        },
        doVillaFlightsSearch: async (apiSearchParams) => {
            return await get('/api/villaflights/search', apiSearchParams, true);
        },
        getRyanairData: async (checkoutCode) => {
            const queryString = new URLSearchParams();
            queryString.append("checkoutCode", checkoutCode);
            return await get(
                "/api/checkout/ryanair",
                queryString,
                false,
                "Confirming with Ryanair"
            );
        },

        getThankYouData: async (checkoutCode) => {
            const queryString = new URLSearchParams();
            queryString.append("checkoutCode", checkoutCode);
            // hideLoading: the thank-you page already shows the booking reference (passed from the
            // payment step) and renders its own placeholders while the details load.
            return await get("/api/checkout/thankyou", queryString, true);
        },
        // Fast half of the payment page (summary straight off the basket) — rendered while the
        // slow prebook/quote call is in flight. Failures are non-fatal: the page falls back to
        // the full load state.
        getPaymentSummary: async (basketId) => {
            const queryString = new URLSearchParams();
            queryString.append("basket", basketId);
            try {
                const response = await fetch(`/api/checkout/payment-summary?${queryString}`);
                if (!response.ok) {
                    return null;
                }
                return await response.json();
            } catch {
                return null;
            }
        },
        getQuoteData: async (checkoutCode) => {
            const queryString = new URLSearchParams();
            queryString.append("checkoutCode", checkoutCode);
            return await get("/api/checkout/quoteinfo", queryString, false, "Saving...");
        },
        submitPaxDetails: async (basketId, paxDetails) => {
            const queryString = new URLSearchParams();
            queryString.append("basket", basketId);
            return await post(
                "/api/checkout/pax",
                queryString,
                paxDetails,
                false,
                "Submitting passenger details"
            );
        },
        getPaxDetails: async (basketId) => {
            const queryString = new URLSearchParams();
            queryString.append("basket", basketId);
            return await get("/api/checkout/pax", queryString, false, "Loading passenger details");
        },
        // Status-aware variant for the payment/summary pages: no global overlay or error modal -
        // the pages render their own staged loading and designed failure states (410 = expired).
        getPaymentDetailsResult: async (basketId) => {
            const queryString = new URLSearchParams();
            queryString.append("basket", basketId);
            const attempt = async () => {
                const response = await fetch(`/api/checkout/payment?${queryString}`);
                if (!response.ok) {
                    return { ok: false, status: response.status, data: null };
                }
                return { ok: true, status: response.status, data: await response.json() };
            };
            try {
                return await attempt();
            } catch {
                // One silent retry: a single dropped request (common on mobile connections)
                // shouldn't dead-end the whole payment page.
                try {
                    return await attempt();
                } catch {
                    return { ok: false, status: 0, data: null };
                }
            }
        },
        createBuilderAgentQuote: async (details) => {
            return await post("/api/basketbuilder/agent-quote", "", details, false, "Creating quote");
        },
        saveAgentQuote: async (checkoutCode, paymentDetails) => {
            const queryString = new URLSearchParams();
            queryString.append("checkoutCode", checkoutCode);
            return await post(
                "/api/checkout/save-as-quote",
                queryString,
                paymentDetails,
                false,
                "Saving quote"
            );
        },
        updatePayment: async (checkoutCode, paymentDetails) => {
            const queryString = new URLSearchParams();
            queryString.append("checkoutCode", checkoutCode);
            // hideLoading: the payment page shows its own in-card "Updating your payment…"
            // status; the global fixed-inset-0 overlay would close the mobile keyboard.
            return await post(
                "/api/checkout/update-payment",
                queryString,
                paymentDetails,
                true
            );
        },
        submitDiscountCode: async (checkoutCode, discountCode, currentPaymentToken) => {
            const queryString = new URLSearchParams();
            queryString.append("checkoutCode", checkoutCode);
            return await post(
                "/api/checkout/apply-discount",
                queryString,
                { discountCode, currentPaymentToken },
                false,
                "Applying discount"
            );
        },
        submitPayment: async (checkoutCode, token) => {
            const queryString = new URLSearchParams();
            queryString.append("checkoutCode", checkoutCode);
            return await post(
                "/api/checkout/payment",
                queryString,
                { token: token },
                false,
                "Submitting payment"
            );
        },
        // incentive: optional { claimIncentive, agentName, agentEmail } (the agent incentive auto-claim).
        confirmAgentBooking: async (checkoutCode, incentive) => {
            const queryString = new URLSearchParams();
            queryString.append("checkoutCode", checkoutCode);
            return await post(
                "/api/checkout/agent-confirm",
                queryString,
                incentive ?? {},
                false,
                "Confirming booking"
            );
        },
        // Whether this basket's booking would earn an agent incentive. Quiet: the incentive is an
        // optional extra, so any failure just hides the offer instead of raising the error overlay.
        getIncentiveOffer: async (basketId) => {
            try {
                const response = await fetch(
                    `/api/checkout/incentive-offer?${new URLSearchParams({ basket: basketId })}`,
                    { headers: { Accept: "application/json" } }
                );
                return response.ok ? await response.json() : null;
            } catch {
                return null;
            }
        },
        confirmRyanair: async (checkoutCode) => {
            const queryString = new URLSearchParams();
            queryString.append("checkoutCode", checkoutCode);
            return await post(
                "/api/checkout/ryanair-confirm",
                queryString,
                {},
                false,
                "Confirming booking"
            );
        },
        getPropertyCalendar: async (
            propertyId,
            departureAirports,
            departureDate,
            monthOffset,
            rooms,
            duration
        ) => {
            const queryString = new URLSearchParams();
            queryString.append("propertyId", propertyId);
            queryString.append("departureAirports", departureAirports);
            queryString.append("departureDate", departureDate);
            queryString.append("monthOffset", monthOffset);
            queryString.append("rooms", rooms);
            queryString.append("duration", duration);

            return await get("/api/package/calendar", queryString, false, "Loading calendar");
        },
        getHotelOnlyCalendar: async (
            propertyId,
            supplier,
            startDate,
            monthOffset,
            rooms,
            duration
        ) => {
            const queryString = new URLSearchParams();
            queryString.append("propertyId", propertyId);
            queryString.append("supplier", supplier);
            queryString.append("startDate", startDate);
            queryString.append("monthOffset", monthOffset);
            queryString.append("rooms", rooms);
            queryString.append("duration", duration);

            return await get("/api/hotelonly/calendar", queryString, false, "Loading calendar");
        },
    };
    return api;
}
